import { FetchQueryOptions } from "@tanstack/react-query";
import z from "zod/v4";

import { env, isProduction } from "@/env";
import { isBrowserNetworkFetchError, isFetchErrorWithStatus } from "@/utils/error";
import { Logger } from "@/utils/logger";
import {
  authenticatedRequest,
  AuthNotReadyError,
  type GetAuthHeaders,
} from "@/utils/signature-auth";

export const DelegationMetadataSchema = z.object({
  name: z.string(),
  address: z.string(),
  isMobileQr: z.boolean().optional(),
});

const DelegationsSchema = z.record(z.string(), DelegationMetadataSchema);

const BaseUserPreferencesSchema = z.object({
  customSubaccountNames: z.record(z.string(), z.string()).optional(),
  maxSlippage: z.number().optional(),
  watchlist: z.array(z.string()).optional(),
  tradingMode: z.enum(["basic", "advanced"]).optional(),
  placeOrderConfirmation: z.boolean().optional(),
  marketCloseConfirmation: z.boolean().optional(),
  subaccountAddress: z.string().optional(),
});

export const UserPreferencesResponseSchema = BaseUserPreferencesSchema.extend({
  accountAddress: z.string(),
  lastUpdated: z.string(),
});

export const SubaccountPreferencesSchema = z.object({
  delegations: DelegationsSchema.optional(),
  subaccountAddress: z.string(),
  lastUpdated: z.string().optional(),
});

export const UserPreferencesWithSubaccountsResponseDataSchema = z.object({
  userPreferences: UserPreferencesResponseSchema,
  subaccounts: z.array(SubaccountPreferencesSchema),
});

export type UserPreferencesWithSubaccountsResponseData = z.infer<
  typeof UserPreferencesWithSubaccountsResponseDataSchema
>;

export interface GetUserPreferencesArgs {
  accountAddress: string;
  getAuthHeaders: GetAuthHeaders;
  subaccountAddress?: string;
}

export const GetUserPreferencesResponseSchema = z.object({
  success: z.boolean(),
  data: UserPreferencesWithSubaccountsResponseDataSchema.optional(),
  message: z.string().optional(),
  error: z.string().optional(),
});

export type GetUserPreferencesResponse = z.infer<typeof GetUserPreferencesResponseSchema>;

export function userPreferencesQueryKey({ accountAddress }: { accountAddress: string }) {
  return ["userPreferences", accountAddress] as const;
}

const GET_USER_PREFERENCES_URL = `${env.NEXT_PUBLIC_API_URL}/api/user-preferences`;

// Local Zustand store is the UI source of truth (see use-user-preferences.ts); the
// remote fetch only catches cross-device drift via lastUpdated timestamp compare.
// 5 min is ample tolerance for that and avoids refetching on every tab refocus.
const USER_PREFERENCES_STALE_TIME = 5 * 60 * 1000;

export function userPreferencesQuery({
  accountAddress,
  getAuthHeaders,
  subaccountAddress,
}: GetUserPreferencesArgs) {
  return {
    queryKey: userPreferencesQueryKey({ accountAddress }),
    staleTime: USER_PREFERENCES_STALE_TIME,
    queryFn: async () => {
      try {
        if (!accountAddress) {
          throw new Error("Address is required");
        }

        const response = await authenticatedRequest.get({
          schema: GetUserPreferencesResponseSchema,
          url: GET_USER_PREFERENCES_URL,
          getAuthHeaders,
          subaccountAddress,
        });

        return response.data.data ?? null;
      } catch (error) {
        // Hydration race; wrapper already logged. Re-runs when enabled flips.
        if (error instanceof AuthNotReadyError) return null;
        // 404 isn't produced by the server handler (it returns 200+null for no-data
        // and 500 on failure). In practice 404 means infra: bad NEXT_PUBLIC_API_URL,
        // CDN routing, or proxy. Warn keeps aggregate volume visibility without
        // paging on per-user noise; a spike in the warning rate is the real signal.
        if (isFetchErrorWithStatus(error, [404])) {
          Logger.warn("user-preferences GET returned 404 (infra-layer)", { accountAddress });
          return null;
        }
        if (isBrowserNetworkFetchError(error)) return null;
        if (isProduction) {
          Logger.error(error, {
            message: "Failed to get user preferences",
            accountAddress,
          });
        } else {
          Logger.warn(
            "The `user-preferences-api` is not accessible, falling back to local storage",
          );
        }
        return null;
      }
    },
  } satisfies FetchQueryOptions<UserPreferencesWithSubaccountsResponseData | null>;
}
